/* eslint-disable max-lines */
import { createClient } from "@lumi/data";
import type { ReactNode, ReactElement } from "react";
import React, { createContext, useContext, useEffect, useMemo, useState } from "react";

type UserType = {
  id: string;
  email: string;
  role: "ADMIN" | "CONTENT_PROVIDER" | "VIEWER";
  displayName?: string;
};

// Helper to decode JWT and extract expiry
function decodeJWT(token: string): { exp?: number } | null {
  try {
    const parts = token.split(".");
    if (parts.length !== 3) return null;
    const payload: unknown = JSON.parse(atob(parts[1]));
    return payload as { exp?: number };
  } catch {
    return null;
  }
}

// Check if token is expired or will expire soon (within 2 minutes)
function isTokenExpired(token: string): boolean {
  const decoded = decodeJWT(token);
  if (!decoded?.exp) return true;
  const now = Math.floor(Date.now() / 1000);
  const buffer = 120; // 2 minute buffer
  return decoded.exp - now < buffer;
}

type LoginRequestType = {
  email: string;
  password: string;
};

type RegisterRequestType = {
  email: string;
  password: string;
};

interface AuthState {
  user: UserType | null;
  accessToken: string | null;
  loading: boolean;
}

interface AuthContextValue extends AuthState {
  signIn: (credentials: LoginRequestType) => Promise<void>;
  signUp: (credentials: RegisterRequestType) => Promise<void>;
  signOut: () => void;
  refreshAuth: () => Promise<boolean>;
  updateUser: (updates: Partial<UserType>) => void;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}

interface AuthProviderProps {
  readonly children: ReactNode;
  readonly apiBase?: string;
}

// Token storage keys
const ACCESS_TOKEN_KEY = "lumi-access-token";
const REFRESH_TOKEN_KEY = "lumi-refresh-token";
const USER_KEY = "lumi-user";

// Helper functions (extracted to reduce component complexity)
function storeAuth(
  accessToken: string,
  refreshToken: string,
  user: UserType,
  setState: React.Dispatch<React.SetStateAction<AuthState>>,
) {
  localStorage.setItem(ACCESS_TOKEN_KEY, accessToken);
  localStorage.setItem(REFRESH_TOKEN_KEY, refreshToken);
  localStorage.setItem(USER_KEY, JSON.stringify(user));
  setState({
    user,
    accessToken,
    loading: false,
  });
}

function clearStoredAuth() {
  localStorage.removeItem(ACCESS_TOKEN_KEY);
  localStorage.removeItem(REFRESH_TOKEN_KEY);
  localStorage.removeItem(USER_KEY);
  // Also remove legacy key for backward compatibility
  localStorage.removeItem("lumi-signed-in");
}

function loadStoredAuth(
  setState: React.Dispatch<React.SetStateAction<AuthState>>,
  refreshAuthCallback: () => Promise<boolean>,
) {
  try {
    const accessToken = localStorage.getItem(ACCESS_TOKEN_KEY);
    const userJson = localStorage.getItem(USER_KEY);

    if (accessToken && userJson) {
      const user = JSON.parse(userJson) as UserType;

      // Check if token is expired or will expire soon
      if (isTokenExpired(accessToken)) {
        console.log("Access token expired, attempting refresh...");
        // Attempt to refresh token
        refreshAuthCallback()
          .then((success) => {
            if (!success) {
              // Refresh failed, clear auth
              clearStoredAuth();
              setState({
                user: null,
                accessToken: null,
                loading: false,
              });
            }
          })
          .catch((error) => {
            console.error("Token refresh failed on mount:", error);
            clearStoredAuth();
            setState({
              user: null,
              accessToken: null,
              loading: false,
            });
          });

        // Set loading state with user but old token
        setState({
          user,
          accessToken,
          loading: true,
        });
      } else {
        // Token is still valid
        setState({
          user,
          accessToken,
          loading: false,
        });
      }
    } else {
      setState({
        user: null,
        accessToken: null,
        loading: false,
      });
    }
  } catch (error) {
    console.error("Error loading stored auth:", error);
    clearStoredAuth();
    setState({
      user: null,
      accessToken: null,
      loading: false,
    });
  }
}

export function AuthProvider({ children, apiBase }: AuthProviderProps): ReactElement {
  // Use environment variable if available, fallback to localhost for development
  const defaultApiBase = import.meta.env.VITE_API_BASE_URL || "http://localhost:3333";
  const actualApiBase = apiBase || defaultApiBase;
  const [state, setState] = useState<AuthState>({
    user: null,
    accessToken: null,
    loading: true,
  });

  const client = createClient(actualApiBase);

  // Sign out
  const signOut = () => {
    clearStoredAuth();
    setState({
      user: null,
      accessToken: null,
      loading: false,
    });
  };

  // Refresh access token using refresh token
  const refreshAuth = async (): Promise<boolean> => {
    try {
      console.log("refreshAuth: Starting refresh...");
      const refreshToken = localStorage.getItem(REFRESH_TOKEN_KEY);
      if (!refreshToken) {
        console.log("refreshAuth: No refresh token found");
        return false;
      }

      console.log("refreshAuth: Calling API refresh endpoint...");
      const response = await client.refreshToken({ refreshToken });
      console.log("refreshAuth: Received new access token:", response.accessToken.slice(0, 20) + "...");

      // Update stored access token
      localStorage.setItem(ACCESS_TOKEN_KEY, response.accessToken);
      setState((prevState) => ({
        ...prevState,
        accessToken: response.accessToken,
        loading: false,
      }));

      console.log("refreshAuth: Token refresh successful");
      return true;
    } catch (error) {
      console.error("refreshAuth: Token refresh failed:", error);
      // If refresh fails, sign out user
      signOut();
      return false;
    }
  };

  // Load stored authentication on mount
  useEffect(() => {
    loadStoredAuth(setState, refreshAuth);
  }, []); // Empty deps OK - only run once on mount

  // Set up periodic token refresh (every 12 minutes for 15-minute tokens)
  useEffect(() => {
    if (!state.accessToken || !state.user) {
      return;
    }

    const refreshInterval = 12 * 60 * 1000; // 12 minutes in milliseconds

    const intervalId = setInterval(() => {
      if (state.accessToken && isTokenExpired(state.accessToken)) {
        console.log("Periodic refresh: token expiring soon, refreshing...");
        void refreshAuth();
      }
    }, refreshInterval);

    return () => clearInterval(intervalId);
  }, [state.accessToken, state.user]); // Stable dependencies - refreshAuth defined above

  // Sign in with email/password
  const signIn = async (credentials: LoginRequestType) => {
    try {
      const response = await client.login(credentials);
      storeAuth(response.accessToken, response.refreshToken, response.user, setState);
    } catch (error) {
      console.error("Sign in failed:", error);
      throw error;
    }
  };

  // Sign up with email/password
  const signUp = async (credentials: RegisterRequestType) => {
    try {
      const response = await client.register(credentials);
      storeAuth(response.accessToken, response.refreshToken, response.user, setState);
    } catch (error) {
      console.error("Sign up failed:", error);
      throw error;
    }
  };

  // Update user data (optimistic update)
  const updateUser = (updates: Partial<UserType>) => {
    setState((prevState) => {
      if (!prevState.user) return prevState;

      const updatedUser = { ...prevState.user, ...updates };
      // Update localStorage as well
      localStorage.setItem(USER_KEY, JSON.stringify(updatedUser));

      return {
        ...prevState,
        user: updatedUser,
      };
    });
  };

  const value: AuthContextValue = useMemo(
    () => ({
      ...state,
      signIn,
      signUp,
      signOut,
      refreshAuth,
      updateUser,
    }),
    [state.user, state.accessToken, state.loading],
  ); // Stable functions, state changes trigger recompute

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

// Helper hook to get authenticated API client
export function useAuthenticatedClient(apiBase?: string) {
  const { accessToken, refreshAuth } = useAuth();

  return React.useMemo(() => {
    const defaultApiBase = import.meta.env.VITE_API_BASE_URL || "http://localhost:3333";

    // Create a refresh callback that returns the new token
    const refreshCallback = async () => {
      const success = await refreshAuth();
      if (success) {
        // Get the updated token from localStorage since refreshAuth updates it
        const newToken = localStorage.getItem("lumi-access-token");
        return newToken ? { accessToken: newToken } : null;
      }
      return null;
    };

    return createClient(apiBase || defaultApiBase, accessToken || undefined, refreshCallback);
  }, [apiBase, accessToken, refreshAuth]);
}
